import React, { useEffect, useState, useRef } from 'react';
import CryptoJS from 'crypto-js';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

const publishMailOAuthCompleted = ({ success, email = '', error = '', resumed = [] }) => {
  try {
    const payload = {
      provider: 'gmail',
      success: Boolean(success),
      email,
      error,
      resumed,
      createdAt: Date.now(),
      nonce: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    };
    localStorage.setItem('mail_oauth_completed', JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('mail_oauth_completed', { detail: payload }));
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('mail_oauth');
      channel.postMessage(payload);
      channel.close();
    }
  } catch (_) {
    // Best-effort fallback for the opener-less OAuth path.
  }
};

const getMailOAuthReturnUrl = () => {
  try {
    return (
      sessionStorage.getItem('mail_oauth_return_url') ||
      localStorage.getItem('mail_oauth_return_url') ||
      '/account/credentials'
    );
  } catch (_) {
    return '/account/credentials';
  }
};

const clearMailOAuthReturnUrl = () => {
  try { sessionStorage.removeItem('mail_oauth_return_url'); } catch (_) { /* ignore */ }
  try { localStorage.removeItem('mail_oauth_return_url'); } catch (_) { /* ignore */ }
};

const getMailOAuthResumeContext = () => {
  try {
    const raw = sessionStorage.getItem('mail_oauth_resume') || localStorage.getItem('mail_oauth_resume');
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
};

/**
 * Gmail OAuth Callback Page
 * -------------------------
 * Google redirects here after the user grants Gmail permission.
 * This page extracts the authorization `code` and `state` from the URL,
 * forwards them to the Core backend to exchange for tokens, and then
 * notifies the opener window (popup parent) of success/failure.
 *
 * NOTE: A useRef guard prevents React 18 StrictMode from sending
 * the authorization code twice (codes are single-use).
 */
export default function GmailCallbackPage() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [gmailEmail, setGmailEmail] = useState(null);
  const [countdown, setCountdown] = useState(3);

  // Guard against React 18 StrictMode double-invocation of useEffect
  const hasExchangedRef = useRef(false);

  useEffect(() => {
    if (hasExchangedRef.current) return;
    hasExchangedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const authError = params.get('error');

    if (authError) {
      const msg = authError === 'access_denied'
        ? 'You did not grant the required permissions. Please try again and make sure to check all permission boxes.'
        : `Google authorization failed: ${authError}`;
      setStatus('error');
      setError(msg);
      if (window.opener) {
        window.opener.postMessage({ type: 'GMAIL_CONNECTED', success: false, error: msg }, '*');
      }
      return;
    }

    if (!code) {
      setStatus('error');
      setError('No authorization code received from Google.');
      return;
    }

    // Build the callback URL (same one used to generate the auth URL)
    const callbackUrl = `${window.location.origin}/gmail-callback`;

    // Send the code to Core backend for token exchange
    const resumeContext = getMailOAuthResumeContext();
    const payload = {
      code,
      state,
      callback_url: callbackUrl,
      pending_mail_auth_action_id: resumeContext.pendingMailAuthActionId || resumeContext.pending_mail_auth_action_id || undefined,
    };
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

    fetch(`${API_URL}/gmail/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: encrypted }),
    })
      .then(async (res) => {
        const responseData = await res.json();

        if (res.ok) {
          // Decrypt response data
          let data = {};
          if (responseData.data && typeof responseData.data === 'string') {
            try {
              const bytes = CryptoJS.AES.decrypt(responseData.data, SECRET_KEY);
              const decrypted = bytes.toString(CryptoJS.enc.Utf8);
              data = JSON.parse(decrypted);
            } catch {
              data = {};
            }
          }

          // Check if this is a pending account switch (needs user confirmation)
          if (data.pending_switch) {
            setGmailEmail(data.new_email || 'new account');
            setStatus('success');

            // Notify the opener that a switch is pending confirmation
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'GMAIL_SWITCH_PENDING',
                  previous_email: data.previous_email,
                  new_email: data.new_email,
                  switch_warning: data.switch_warning,
                },
                '*'
              );
            }

            // Auto-close popup — confirmation happens in the parent window
            let count = 3;
            const timer = setInterval(() => {
              count -= 1;
              setCountdown(count);
              if (count <= 0) {
                clearInterval(timer);
                window.close();
              }
            }, 1000);
            return;
          }

          setGmailEmail(data.gmail_email || 'Gmail');
          setStatus('success');
          publishMailOAuthCompleted({
            success: true,
            email: data.gmail_email || '',
            resumed: data.pending_mail_auth_resumed || [],
          });

          // Notify the opener (popup parent) that Gmail is connected
          if (window.opener) {
            window.opener.postMessage(
              {
                  type: 'GMAIL_CONNECTED',
                  success: true,
                  gmail_email: data.gmail_email,
                  pending_mail_auth_resumed: data.pending_mail_auth_resumed || [],
                },
              '*'
            );
          }

          // Auto-close with countdown
          let count = 3;
          const timer = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              if (window.opener) {
                window.close();
              } else {
                const returnUrl = getMailOAuthReturnUrl();
                clearMailOAuthReturnUrl();
                window.location.replace(returnUrl);
              }
            }
          }, 1000);
        } else {
          let errorMsg = responseData.error || 'Failed to connect Gmail. Please try again.';
          // Clean up prefixed error codes for user-friendly display
          errorMsg = errorMsg
            .replace(/^MISSING_PERMISSIONS:\s*/i, '')
            .replace(/^GOOGLE_PERMISSION_DENIED:\s*/i, '')
            .replace(/^GOOGLE_AUTH_EXPIRED:\s*/i, '');
          setStatus('error');
          setError(errorMsg);
          publishMailOAuthCompleted({ success: false, error: errorMsg });

          // Notify parent of failure
          if (window.opener) {
            window.opener.postMessage(
              { type: 'GMAIL_CONNECTED', success: false, error: errorMsg },
              '*'
            );
          }
        }
      })
      .catch((err) => {
        setStatus('error');
        setError(`Network error: ${err.message}`);
        publishMailOAuthCompleted({ success: false, error: err.message });
        if (window.opener) {
          window.opener.postMessage(
            { type: 'GMAIL_CONNECTED', success: false, error: err.message },
            '*'
          );
        }
      });
  }, []);

  return (
    <div style={styles.page}>
      {/* Animated background shapes */}
      <div style={styles.bgShape1} />
      <div style={styles.bgShape2} />

      <div style={styles.card}>
        {/* Gmail icon */}
        <div style={styles.iconWrap}>
          {status === 'processing' && (
            <div style={styles.spinnerOuter}>
              <div style={styles.spinnerInner} />
              <svg style={styles.gmailIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" stroke="#EA4335" strokeWidth="1.5" />
                <path d="M22 6L12 13L2 6" stroke="#EA4335" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          )}
          {status === 'success' && (
            <div style={styles.successCircle}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {status === 'error' && (
            <div style={styles.errorCircle}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 style={styles.title}>
          {status === 'processing' && 'Connecting Gmail'}
          {status === 'success' && 'Gmail Connected!'}
          {status === 'error' && 'Connection Failed'}
        </h1>

        {/* Subtitle */}
        <p style={styles.subtitle}>
          {status === 'processing' && 'Setting up your Gmail integration. This will only take a moment…'}
          {status === 'success' && `Your Gmail (${gmailEmail}) is now connected for sending emails.`}
          {status === 'error' && (error || 'Something went wrong. Please try again.')}
        </p>

        {/* Footer / action area */}
        {status === 'processing' && (
          <div style={styles.progressBar}>
            <div style={styles.progressFill} />
          </div>
        )}

        {status === 'success' && (
          <p style={styles.closeNote}>
            This window will close in <strong>{countdown}s</strong>
          </p>
        )}

        {status === 'error' && (
          <button
            style={styles.retryBtn}
            onClick={() => window.close()}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#c5221f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#EA4335'; }}
          >
            Close Window
          </button>
        )}
      </div>

      {/* Inline keyframe animations */}
      <style>{`
        @keyframes gmailSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes progressSlide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes floatA {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(20px,-30px) scale(1.05); }
        }
        @keyframes floatB {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-20px,20px) scale(1.08); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ─── styles ──────────────────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #fce4e4 0%, #fef3e2 50%, #e8f5e9 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  bgShape1: {
    position: 'absolute',
    width: 340,
    height: 340,
    borderRadius: '50%',
    background: 'rgba(234,67,53,0.08)',
    top: -80,
    left: -80,
    animation: 'floatA 8s ease-in-out infinite',
  },
  bgShape2: {
    position: 'absolute',
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'rgba(66,133,244,0.08)',
    bottom: -60,
    right: -60,
    animation: 'floatB 10s ease-in-out infinite',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    padding: '48px 40px 40px',
    maxWidth: 420,
    width: '90%',
    textAlign: 'center',
    animation: 'fadeInUp 0.5s ease-out',
  },
  iconWrap: {
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  spinnerOuter: {
    position: 'relative',
    width: 64,
    height: 64,
  },
  spinnerInner: {
    position: 'absolute',
    inset: 0,
    border: '3px solid #f0f0f0',
    borderTopColor: '#EA4335',
    borderRadius: '50%',
    animation: 'gmailSpin 1s linear infinite',
  },
  gmailIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 28,
    height: 28,
    transform: 'translate(-50%,-50%)',
  },
  successCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#34A853',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#EA4335',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: '0 0 8px',
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a1a',
  },
  subtitle: {
    margin: '0 0 24px',
    fontSize: 14,
    color: '#666',
    lineHeight: 1.5,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    background: '#f0f0f0',
    overflow: 'hidden',
    position: 'relative',
  },
  progressFill: {
    position: 'absolute',
    inset: 0,
    width: '40%',
    background: 'linear-gradient(90deg, #EA4335, #FBBC05, #34A853, #4285F4)',
    borderRadius: 2,
    animation: 'progressSlide 1.4s ease-in-out infinite',
  },
  closeNote: {
    fontSize: 13,
    color: '#999',
    margin: 0,
  },
  retryBtn: {
    display: 'inline-block',
    padding: '10px 28px',
    background: '#EA4335',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
};
